import { usePasteFromToolbar } from '@components/ArtBoard/ArtboardToolbar'
import { getCanvasState } from '@components/ArtBoard/Canvas/helpers/canvas'
import { useClickOutside } from '@lib/hooks'
import type {
  ID,
  SectionItem,
  TextSize,
} from '@octopus-do/octopus-project-core'
import { thunkDuplicateSection } from '@store/migrationActions/thunkActions/thunkSectionsActions'
import { useProjectLegacyActions } from '@store/migrationActions/useProjectLegacyActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import { CanvasActionTypes } from '@storeOld/reducers/canvas/canvas'
import { getPreviewColor } from '@storeOld/reducers/canvas/selectors'
import type React from 'react'
import { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtboardMode, useProjectAccess } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { SECTIONS_PERMISSION } from '@/permissions/project/definitions'
import { isInsidePopover } from '@/utils'

export const useSectionRow = (
  section: SectionItem,
  id: ID,
  index: number,
  last: boolean
) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const {
    changeSectionTitle,
    changeSectionTextSize,
    collapseSections,
    changeSectionColor,
    deleteSections,
    moveSectionUp,
    moveSectionDown,
  } = useProjectLegacyActions()

  const { gallery, seoMode } = useArtboardMode()

  const { title: name, colorId, collapsed, textSize } = section
  const [sectionName, setSectionName] = useState(name)

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const disabledUp = index === 0
  const disabledDown = last

  const isSelected = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.section.selectedSectionId === id
  )

  const artboardDisabled = useArtBoardDisabled()

  // The title input may already have focus when the artboard goes off.
  const isEditing = useSelector<AppState, boolean>(
    ({ canvas }) =>
      !artboardDisabled && canvas.section.selectedSectionEditingId === id
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const previewColor = useSelector<AppState, string | null>(
    (state) => getPreviewColor(state, id),
    shallowEqual
  )

  const { scale } = getCanvasState(
    document.getElementById('gestureMovableContainer')
  )

  const { hasNotAvailableAccess } = useProjectAccess(
    SECTIONS_PERMISSION.TEXT_SIZE
  )

  const setActionToolbar = () => {
    dispatch({
      type: CanvasActionTypes.SELECT_SECTION,
      payload: id,
    })
  }

  const setIsEditing = () => {
    dispatch({
      type: CanvasActionTypes.SELECT_TO_EDIT_SECTION,
      payload: id,
    })
  }

  const clearSelected = () => {
    dispatch({
      type: CanvasActionTypes.CLEAR_SELECTED_SECTION,
    })
  }

  const handleTitleUpdate = () => {
    if (sectionName !== section.title) {
      changeSectionTitle({ id, title: sectionName })
      clearSelected()
    }
  }

  const handleClickOut = (e: React.MouseEvent) => {
    if (isInsidePopover(e.target)) {
      return
    }

    handleTitleUpdate()
    clearSelected()
  }

  const clickOutside = useClickOutside<HTMLDivElement>(
    isSelected,
    handleClickOut,
    () => {}
  )

  const handleMoveUpClick = () => {
    if (disabledUp) {
      return
    }

    moveSectionUp({ id })
  }

  const handleMoveDownClick = () => {
    if (disabledDown) {
      return
    }

    moveSectionDown({ id })
  }

  const handleDeleteClick = () => {
    deleteSections({ ids: [id] })
  }

  const handleDuplicateClick = () => {
    dispatch(thunkDuplicateSection(id))
  }

  const { onPaste: handlePasteClick, pasting } = usePasteFromToolbar('section')

  const handleColorChange = (colorId: ID) => {
    changeSectionColor({ id, colorId })
  }

  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    const isModeDisabled = isReadOnly || gallery || seoMode

    if (isModeDisabled) {
      return
    }

    if (!isSelected) {
      setActionToolbar()
      return
    }

    if (!isEditing) {
      setIsEditing()
    }
  }

  const handleCollapseClick = (e: React.MouseEvent) => {
    e.stopPropagation()

    collapseSections({ ids: [id], collapsed: !collapsed })
  }

  const handleChangeTextSize = (v: TextSize) => {
    changeSectionTextSize({ id, textSize: v })
  }

  const disabledCss = isReadOnly || gallery || seoMode

  return {
    textSize,
    colorHex,
    previewColor,
    scale,
    hasNotAvailableAccess,
    disabledCss,
    collapsed,
    isEditing,
    sectionName,
    disabledUp,
    disabledDown,
    colorId,
    isSelected,
    name,
    setSectionName,
    clickOutside,
    handleMoveUpClick,
    handleMoveDownClick,
    handleDeleteClick,
    handleDuplicateClick,
    handlePasteClick,
    pasting,
    handleColorChange,
    handleTitleClick,
    handleCollapseClick,
    handleChangeTextSize,
    handleTitleUpdate,
  }
}
